import { useMemo } from "react";
import type { ThemeClasses, TrackingReplay } from "../types";
import { DENSE_ROW, ICON_BTN, ICON_BTN_DANGER } from "../../../../shared";

interface TrackingReplayListRowProps {
  replay: TrackingReplay;
  themeClasses: ThemeClasses;
  onView: (replay: TrackingReplay) => void;
  onDelete: (replay: TrackingReplay) => void;
  isDeleting?: boolean;
}

function formatDuration(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffMs = endDate.getTime() - startDate.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function TrackingReplayListRow({
  replay,
  themeClasses,
  onView,
  onDelete,
  isDeleting,
}: TrackingReplayListRowProps) {
  const duration = useMemo(
    () => formatDuration(replay.start_date, replay.end_date),
    [replay.start_date, replay.end_date]
  );

  const positionCount = replay.positions?.length || 0;

  const vehicleShort = `${replay.vehicle_id.slice(0, 8)}...`;
  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
        <i className="ri-route-line text-primary-600 text-base"></i>
      </div>
      <div className="min-w-0 flex-1">
        <h3 className={`text-sm font-semibold ${themeClasses.text} truncate`} title={replay.name}>
          {replay.name}
        </h3>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {positionCount} puntos • {duration}
          <span className="md:hidden">{` • ${vehicleShort}`}</span>
        </p>
      </div>
      <div className="hidden md:block w-24 shrink-0 min-w-0" title={replay.vehicle_id}>
        <p className={`text-[11px] ${themeClasses.textSecondary}`}>Vehículo</p>
        <p className={`text-sm font-mono ${themeClasses.text} truncate`}>{vehicleShort}</p>
      </div>
      <div className="hidden lg:block w-52 shrink-0 min-w-0">
        <p className={`text-[11px] ${themeClasses.textSecondary}`}>Periodo</p>
        <p className={`text-xs ${themeClasses.text} truncate tabular-nums`}>
          {formatDate(replay.start_date)} - {formatDate(replay.end_date)}
        </p>
      </div>

      <div className="flex items-center gap-0.5 shrink-0">
        <button
          onClick={() => onView(replay)}
          className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
          aria-label="Ver"
          title="Ver"
        >
          <i className="ri-eye-line"></i>
        </button>
        <button
          onClick={() => onDelete(replay)}
          disabled={isDeleting}
          className={ICON_BTN_DANGER}
          aria-label="Eliminar"
          title="Eliminar"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </div>
    </div>
  );
}
